import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import { useReloadCameras } from 'camera-module/cameras/core/useCameras'
import BigCheckIcon from 'assets/icons/navigation/BigCheckIcon'
import cameraActions from 'camera-module/camera/core/cameraActions'
import filtersState from 'photo-module/filters/core/filtersState'
import useQueryParams from 'shared-module/url/useQueryParams'
import CameraTile from 'camera-module/cameras/ui/cameras-page/CameraTile'
import { useCameraPlansActions } from 'camera-module/camera/core/transmission-plan/core/cameraPlans.hook'
import CAppBar from 'storybook-component-module/src/components/containers/CAppBar'
import CButton from 'storybook-component-module/src/components/buttons/CButton'
import { useHistory } from 'react-router-dom'
import appConfig from 'shared-module/config/appConfig'
import switches from 'shared-module/config/switches'
import useAllSharedCameras from 'camera-module/camera/core/useAllSharedCameras'
import useFetchingSharedCameras from 'camera-module/camera/core/useFetchingSharedCameras'
import cameraSharingActions from 'camera-module/camera/core/CameraSharingActions'
import useOwnedSharedCameras from 'camera-module/camera/core/useOwnedSharedCameras'
import useSharedCameras from 'camera-module/camera/core/useSharedCameras'
import Grid from '@material-ui/core/Grid'
import CTypography from 'storybook-component-module/src/components/typographies/CTypography'
import useFetchingPendingSharedCameras from 'camera-module/camera/core/useFetchingPendingSharedCameras'

const useStyles = makeStyles((theme) => ({
  icon: {
    width: '1.25rem',
    paddingRight: 0,
  },
  title: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.125rem',
      marginLeft: '-0.5rem',
    },
  },
  addButton: {
    minWidth: 'auto',
    whiteSpace: 'noWrap',
    [theme.breakpoints.down('xs')]: {
      fontSize: '12px',
      padding: '0.25rem 0.5rem',
    },
  },
  paperWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
  },
  paperNoCamera: {
    marginTop: '50px',
    padding: '20px',
    margin: '110px 20px',
    justifyContent: 'center',
    textAlign: 'center',
  },
  content: {
    width: '100%',
  },
  noCameraMessage: {
    padding: '20px',
    color: theme.palette.text.primary,
    textAlign: 'left',
    fontSize: '16px',
  },
  paperContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '1.25rem',
    padding: '1.25rem',
    [theme.breakpoints.down('xs')]: {
      margin: '1rem 0',
      padding: '1rem 0.5rem 1rem 1rem',
    },
  },
  noCamerasTextTitle: {
    textAlign: 'center',
    marginTop: '75px',
    marginBottom: '20px',
    fontFamily: 'ibm-plex-sans-regular',
    color: '#B1AFAF',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  noCamerasTextContent: {
    textAlign: 'center',
    marginBottom: '75px',
    fontFamily: 'ibm-plex-sans-regular',
    color: '#B1AFAF',
    fontSize: '14px',
    fontWeight: 400,
  },
}))

const CameraListPanel = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpenAddCameraDialog, setIsOpenAddCameraDialog] = useState(false)
  const queryParams = useQueryParams()
  const cameraPlansActions = useCameraPlansActions()
  const history = useHistory()
  const cameras = useReloadCameras()
  const AllSharedCameras = useAllSharedCameras()
  const ownedSharedCameras = useOwnedSharedCameras()
  const sharedCameras = useSharedCameras()
  const FetchingSharedCameras = useFetchingSharedCameras()
  const FetchingPendingSharedCameras = useFetchingPendingSharedCameras()
  const result = AllSharedCameras.reduce((accumulator, current) => {
    const exists = accumulator.find(item => {
      return item.config.id === current.config.id
    })
    if (!exists) {
      accumulator = accumulator.concat(current)
    }
    return accumulator
  }, [])

  const pageTitle = queryParams.transmission_plan
    ? t('app:purchasing_flow.transmission_plan.select_device')
    : t('menu.select_camera')

  useEffect(() => {
    dispatch(cameraActions.clear())
    dispatch(cameraPlansActions.reset())
    dispatch(filtersState.clear())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    dispatch(cameraSharingActions.fetchAllSharedCameras())
    dispatch(cameraSharingActions.fetchOwnedSharedCameras())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const toggleAddCamera = () => {
    history.push({
      pathname: '/add-camera',
      state: {
        data: true,
      },
    })
    setIsOpenAddCameraDialog(!isOpenAddCameraDialog)
  }

  const renderNoCamerasContainer = () => (
    <Paper className={classes.paperContainer}>
      <Grid container display="flex" justifyContent="center">
        <Grid item md={6} xs={12}>
          <CTypography variant="body2" color="textPrimary" className={classes.noCamerasTextTitle}>{ t('camera_list.empty_state_title') }</CTypography>
          <CTypography variant="body2" className={classes.noCamerasTextContent}>{ t('camera_list.empty_state_subTitle') }</CTypography>
        </Grid>
      </Grid>
    </Paper>
  )
  const renderCameras = () => {
    if (!cameras) {
      return (
        <Paper className={classes.paper} elevation={1}>
          <div className=" center-align">{ t('camera_list.loading') }</div>
        </Paper>
      )
    }

    return (
      <Paper className={classes.paper} elevation={1}>
        { sharedCameras.length === result.length && result?.map((camera) => (
          <CameraTile ownedSharedCameras={ownedSharedCameras} isShared isSpypoint key={camera.id} camera={camera} />
        )) }
      </Paper>
    )
  }

  return (
    <div className={classes.root}>
      <CAppBar Icon={BigCheckIcon} title={pageTitle} titleClassName={classes.title} iconClassName={classes.icon}>
        <CButton variety="secondary" color="primary" className={classes.addButton} onClick={toggleAddCamera}>
          { t('app:active_device.activate_camera') }
        </CButton>
      </CAppBar>
      { cameras && !FetchingSharedCameras && sharedCameras?.length === 0 && renderNoCamerasContainer() }
      { renderCameras() }
    </div>
  )
}

export default CameraListPanel
